/**
 * What the app remembers between drives: totals, fill-ups and a log of trips.
 *
 * Settings and dashboards live in localStorage, which is small, synchronous
 * and fine for them. This is the other half -- data that grows with every
 * drive -- so it goes in IndexedDB, and everything is keyed by car: two cars
 * on one phone keep separate lifetime figures and fuel logs.
 *
 * Every call is guarded. A browser in private mode, with storage blocked, or
 * simply out of quota, must cost the driver a dashboard that forgets things,
 * never a dashboard that fails to open.
 */
import type { FuelFill, JourneyState, TripRecord, VehicleIdentity } from '@obd/core';
import { carKey, compareIdentities, isMismatch } from '@obd/core';

const DB_NAME = 'dafer-obd2';
const DB_VERSION = 1;

/** Trips kept per car. At a few hundred bytes each this is years of driving. */
const MAX_TRIPS = 1000;

export interface StoredTrip extends TripRecord {
  /** Unique, and sorts by time. */
  id: string;
  carKey: string;
}

export interface CarRecords {
  totals?: JourneyState;
  fills: FuelFill[];
  trips: StoredTrip[];
}

/** The bucket used before any car has been identified. */
export const UNKNOWN_CAR = 'unknown';

export const keyForCar = (car: VehicleIdentity | undefined): string => (car ? carKey(car) : UNKNOWN_CAR);

type StoreName = 'kv' | 'fills' | 'trips';

/**
 * The two ways this data can be held.
 *
 * IndexedDB is the real one. The in-memory fallback is for a browser that has
 * no IndexedDB or refuses to open it -- private windows, blocked site data --
 * where the alternative would be a dashboard that silently forgets a figure
 * the moment it is written. In memory it at least stays consistent for as long
 * as the app is open, and `storageReport().durable` says which is in use.
 */
interface Backend {
  durable: boolean;
  get<T>(store: StoreName, key: string): Promise<T | undefined>;
  put(store: StoreName, value: unknown, key?: string): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
  byCar<T>(store: 'fills' | 'trips', carKey: string): Promise<T[]>;
}

let backendPromise: Promise<Backend> | null = null;

function backend(): Promise<Backend> {
  if (!backendPromise) backendPromise = openIdb().then((db) => (db ? idbBackend(db) : memoryBackend()));
  return backendPromise;
}

function openIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
        if (!db.objectStoreNames.contains('fills')) {
          db.createObjectStore('fills', { keyPath: 'key' }).createIndex('car', 'carKey');
        }
        if (!db.objectStoreNames.contains('trips')) {
          db.createObjectStore('trips', { keyPath: 'id' }).createIndex('car', 'carKey');
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
      // A browser that never answers must not hang the load either.
      setTimeout(() => resolve(null), 3000);
    } catch {
      resolve(null);
    }
  });
}

function idbBackend(db: IDBDatabase): Backend {
  const run = <T>(
    store: StoreName,
    mode: IDBTransactionMode,
    body: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T | undefined> =>
    new Promise((resolve) => {
      try {
        const tx = db.transaction(store, mode);
        const request = body(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(undefined);
        tx.onerror = () => resolve(undefined);
        tx.onabort = () => resolve(undefined);
      } catch {
        resolve(undefined);
      }
    });

  return {
    durable: true,
    get: <T>(store: StoreName, key: string) => run<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>),
    put: async (store, value, key) => {
      await run(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)));
    },
    remove: async (store, key) => {
      await run(store, 'readwrite', (s) => s.delete(key));
    },
    byCar: <T>(store: 'fills' | 'trips', carKey: string) =>
      run<T[]>(store, 'readonly', (s) => s.index('car').getAll(carKey) as IDBRequest<T[]>).then((r) => r ?? []),
  };
}

function memoryBackend(): Backend {
  const stores = new Map<StoreName, Map<string, unknown>>([['kv', new Map()], ['fills', new Map()], ['trips', new Map()]]);
  const of = (store: StoreName): Map<string, unknown> => stores.get(store) as Map<string, unknown>;
  const keyOf = (store: StoreName, value: unknown, key?: string): string => {
    if (key !== undefined) return key;
    const row = value as { key?: string; id?: string };
    return store === 'fills' ? row.key ?? '' : row.id ?? '';
  };
  return {
    durable: false,
    get: async <T>(store: StoreName, key: string) => of(store).get(key) as T | undefined,
    put: async (store, value, key) => {
      of(store).set(keyOf(store, value, key), value);
    },
    remove: async (store, key) => {
      of(store).delete(key);
    },
    byCar: async <T>(store: 'fills' | 'trips', carKey: string) =>
      [...of(store).values()].filter((row) => (row as { carKey?: string }).carKey === carKey) as T[],
  };
}

const byCar = async <T>(store: 'fills' | 'trips', key: string): Promise<T[]> =>
  (await backend()).byCar<T>(store, key);

/** Everything stored for one car. Missing pieces come back empty. */
export async function loadCar(key: string): Promise<CarRecords> {
  const db = await backend();
  const [totals, fills, trips] = await Promise.all([
    db.get<JourneyState>('kv', 'totals:' + key),
    byCar<{ key: string; carKey: string; fill: FuelFill }>('fills', key),
    byCar<StoredTrip>('trips', key),
  ]);
  return {
    ...(totals ? { totals } : {}),
    fills: fills.map((row) => row.fill).sort((a, b) => a.t - b.t),
    trips: trips.sort((a, b) => b.endedAt - a.endedAt),
  };
}

export async function saveTotals(key: string, totals: JourneyState): Promise<void> {
  await (await backend()).put('kv', totals, 'totals:' + key);
}

export async function saveFill(key: string, fill: FuelFill): Promise<void> {
  await (await backend()).put('fills', { key: key + ':' + fill.id, carKey: key, fill });
}

export async function deleteFill(key: string, id: string): Promise<void> {
  await (await backend()).remove('fills', key + ':' + id);
}

export async function saveTrip(trip: StoredTrip): Promise<void> {
  const db = await backend();
  await db.put('trips', trip);
  // Prune oldest first, so the log cannot grow without bound.
  const trips = await byCar<StoredTrip>('trips', trip.carKey);
  if (trips.length <= MAX_TRIPS) return;
  const oldest = trips.sort((a, b) => a.endedAt - b.endedAt).slice(0, trips.length - MAX_TRIPS);
  for (const old of oldest) await db.remove('trips', old.id);
}

export async function clearTrips(key: string): Promise<void> {
  const db = await backend();
  for (const trip of await byCar<StoredTrip>('trips', key)) await db.remove('trips', trip.id);
}

/**
 * Move everything recorded before the car was identified onto that car.
 *
 * The first drive usually happens before a VIN has been read, and losing it
 * would be a silly way to start a car's lifetime figures.
 */
export async function adoptRecords(from: string, to: string): Promise<CarRecords | undefined> {
  if (from === to) return undefined;
  const records = await loadCar(from);
  const empty = !records.totals && records.fills.length === 0 && records.trips.length === 0;
  if (empty) return undefined;

  if (records.totals) await saveTotals(to, records.totals);
  for (const fill of records.fills) {
    await saveFill(to, fill);
    await deleteFill(from, fill.id);
  }
  const db = await backend();
  for (const trip of records.trips) {
    await saveTrip({ ...trip, carKey: to });
    await db.remove('trips', trip.id);
  }
  await db.remove('kv', 'totals:' + from);
  return records;
}

/**
 * The key this car's records live under, remembering the car so the next
 * connection finds them again.
 *
 * Cars without a VIN are matched the same tolerant way the rest of the app
 * matches them, because an exact fingerprint key would file the same car under
 * a new name if one sensor stopped answering.
 */
export async function resolveCar(id: VehicleIdentity | undefined): Promise<string> {
  if (!id) return UNKNOWN_CAR;
  const known = await knownCars();
  if (id.vin) {
    const key = carKey(id);
    if (!known[key]) await rememberCar(known, key, id);
    return key;
  }
  for (const [key, other] of Object.entries(known)) {
    const match = compareIdentities(id, other);
    if (!isMismatch(match) && match.verdict !== 'unknown') return key;
  }
  const key = carKey(id);
  await rememberCar(known, key, id);
  return key;
}

async function rememberCar(known: Record<string, VehicleIdentity>, key: string, id: VehicleIdentity): Promise<void> {
  await (await backend()).put('kv', { ...known, [key]: id }, 'cars');
}

/** Cars this device has records for. */
export async function knownCars(): Promise<Record<string, VehicleIdentity>> {
  return (await (await backend()).get<Record<string, VehicleIdentity>>('kv', 'cars')) ?? {};
}

export interface StorageReport {
  /** True when the browser has promised not to evict this data. */
  persisted: boolean;
  /** False when this browser gave us no database, so data lasts only this session. */
  durable: boolean;
  usageBytes?: number;
  quotaBytes?: number;
}

/**
 * Ask the browser to keep this data.
 *
 * Without it, "best effort" storage can be cleared when the device is short of
 * space -- taking gear ratios, fuel history and lifetime totals with it.
 * Installed apps are usually granted it without a prompt.
 */
export async function requestPersistentStorage(): Promise<StorageReport> {
  const report: StorageReport = { persisted: false, durable: (await backend()).durable };
  try {
    if (!navigator.storage) return report;
    report.persisted = (await navigator.storage.persisted?.()) ?? false;
    if (!report.persisted && navigator.storage.persist) {
      report.persisted = await navigator.storage.persist();
    }
    const estimate = await navigator.storage.estimate?.();
    if (estimate?.usage !== undefined) report.usageBytes = estimate.usage;
    if (estimate?.quota !== undefined) report.quotaBytes = estimate.quota;
  } catch {
    /* storage is blocked; the app still runs, it just forgets more easily */
  }
  return report;
}

export async function storageReport(): Promise<StorageReport> {
  const report: StorageReport = { persisted: false, durable: (await backend()).durable };
  try {
    report.persisted = (await navigator.storage?.persisted?.()) ?? false;
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.usage !== undefined) report.usageBytes = estimate.usage;
    if (estimate?.quota !== undefined) report.quotaBytes = estimate.quota;
  } catch {
    /* nothing to report */
  }
  return report;
}
